import React, { useContext, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Close } from '@mui/icons-material';
import { Slide } from '@mui/material';
import type { Editor } from '@tiptap/react';
import type { Doc as YDoc } from 'yjs';

import { useStore } from 'utils/hooks/store';
import { CloseDrawerButton } from 'components/Entities/EntitySubmissionTab/SubmissionHistory/styles';
import {
  observeDocumentSettings,
  readDocumentSettings,
  writeDocumentSettings,
} from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';
import { FileUploadContext } from 'containers/Tiptap/components/tiptap-node/image-upload-node/image-upload-node';
import { applyDifferentFirstPage } from 'containers/Tiptap/hooks/use-document-settings-sync';
import { useHeaderFooterInserts } from 'containers/Tiptap/hooks/use-header-footer-inserts';
import { HEADER_FOOTER_CLOSE_ATTR } from 'containers/Tiptap/hooks/use-header-footer-keys';
import { useOpenHeaderFooter } from 'containers/Tiptap/hooks/use-header-footer-overlay-editor';
import { useHeaderFooterPanelHeight } from 'containers/Tiptap/hooks/use-header-footer-panel-height';
import { useEditorStore } from 'containers/Tiptap/hooks/useEditorStore';
import { HEADER_FOOTER_PANEL_ATTR } from 'containers/Tiptap/lib/header-footer-dom';
import { HeaderFooterZone, ZONE_LABELS } from 'containers/Tiptap/lib/header-footer-zone';

import { HeaderFooterDetailsSection, HeaderFooterInsertSection, HeaderFooterLayoutSection } from './HeaderFooterPanelSections';
import {
  HeaderFooterEditingStackStyles,
  StyledDockedPanel,
  StyledPanelBody,
  StyledPanelClip,
  StyledPanelHeader,
  StyledPanelTitle,
} from './styles';

interface ShownZone {
  zone: HeaderFooterZone;
  pageNumber: number;
}

const readDifferentFirstPage = (ydoc: YDoc): boolean => readDocumentSettings(ydoc).header?.differentFirstPage ?? false;

const useDifferentFirstPage = (ydoc: YDoc | null): boolean => {
  const [value, setValue] = useState(() => (ydoc ? readDifferentFirstPage(ydoc) : false));

  useEffect(() => {
    if (!ydoc) return undefined;
    const sync = (): void => setValue(readDifferentFirstPage(ydoc));
    sync();
    return observeDocumentSettings(ydoc, sync);
  }, [ydoc]);

  return value;
};

interface PanelBodyProps {
  editor: Editor;
  ydoc: YDoc;
  differentFirstPage: boolean;
}

const PanelBody = observer(({ editor, ydoc, differentFirstPage }: PanelBodyProps) => {
  const { session } = useStore();
  const { entityId } = useContext(FileUploadContext);
  const { insertToken, insertImageFile, insertCompanyLogo } = useHeaderFooterInserts(editor, entityId);
  const editorStore = useEditorStore();

  const handleDifferentFirstPage = (value: boolean): void => {
    writeDocumentSettings(ydoc, applyDifferentFirstPage(readDocumentSettings(ydoc), editor.storage.pages, value));
    editor.commands.setDifferentFirstPage(value);
    editorStore.setHasUnpublishedChanges(true);
  };

  return (
    <StyledPanelBody>
      <HeaderFooterLayoutSection
        differentFirstPage={differentFirstPage}
        onDifferentFirstPageChange={handleDifferentFirstPage}
      />
      <HeaderFooterInsertSection
        hasCompanyLogo={session.hasOrganisationLogo}
        onInsertImage={insertImageFile}
        onInsertCompanyLogo={insertCompanyLogo}
      />
      <HeaderFooterDetailsSection onInsert={insertToken} />
    </StyledPanelBody>
  );
});


export const HeaderFooterPanel = observer(({ editor }: { editor: Editor }) => {
  const { ydoc } = useEditorStore();
  const open = useOpenHeaderFooter(editor);
  const differentFirstPage = useDifferentFirstPage(ydoc);
  const clipRef = useRef<HTMLDivElement>(null);

  const [shown, setShown] = useState<ShownZone | null>(null);
  const [isIn, setIsIn] = useState(false);

  const openZone = open?.zone ?? null;
  const openPageNumber = open?.pageNumber ?? null;
  useEffect(() => {
    if (openZone && openPageNumber !== null) {
      setShown({ zone: openZone, pageNumber: openPageNumber });
      setIsIn(true);
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => setIsIn(false));
    return () => window.cancelAnimationFrame(frame);
  }, [openZone, openPageNumber]);

  const height = useHeaderFooterPanelHeight(isIn);

  if (!shown || !ydoc) return null;

  const zoneLabels = ZONE_LABELS[shown.zone];
  const title = differentFirstPage && shown.pageNumber === 1 ? zoneLabels.firstPageLabel : zoneLabels.label;

  return (
    <StyledPanelClip ref={clipRef} $height={height}>
      {isIn && <HeaderFooterEditingStackStyles />}
      <Slide
        direction="left"
        in={isIn}
        appear
        mountOnEnter
        unmountOnExit
        container={() => clipRef.current as Element}
        onExited={() => setShown(null)}
      >
        <StyledDockedPanel
          {...{ [HEADER_FOOTER_PANEL_ATTR]: true }}
          data-testid="header-footer-panel"
          aria-labelledby="header-footer-panel-title"
        >
          <StyledPanelHeader>
            <StyledPanelTitle id="header-footer-panel-title" data-testid="header-footer-panel-title">
              {title}
            </StyledPanelTitle>
            <CloseDrawerButton
              {...{ [HEADER_FOOTER_CLOSE_ATTR]: true }}
              data-testid="header-footer-panel-close"
              onClick={() => editor.commands.closeHeaderFooterEditors()}
              aria-label={`Stop editing ${title.toLowerCase()}`}
            >
              <Close />
            </CloseDrawerButton>
          </StyledPanelHeader>
          <PanelBody editor={editor} ydoc={ydoc} differentFirstPage={differentFirstPage} />
        </StyledDockedPanel>
      </Slide>
    </StyledPanelClip>
  );
});
